import { DEFENCE_ITEM_ID_ASSAULT_VEST, DEFENCE_ITEM_ID_SINKANOKISEKI } from "../../types/item";
import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_SPECIAL, isKataYaburiMove, isPhotonGeyserMove, isPsyshockMove, isTerabrastMove, isThroughRankMove } from "../../move/move";
import { PokemonV2, hasPokemonStatus } from "../../pokemon/pokemon";
import { hasKodaiOrQuark, isFurCoat, isKusanokegawa, isMarvelScale, isTennen, isWazawainoturugi, isWazawainoutama } from "../ability";
import { isNotLastEvolvePokemon } from "../pokemon/evolution";
import { calcRankedValue, calc_五捨五超入_gosutegoire } from "./core-utils";
import type { damageRateMapper } from "../../types/damage";
import { per_075, per_150, per_130 } from "./constants";
import { BATTLE_FIELD_GLASS } from "../../OtherSetting";

// 【5】防御の補正値
export function calc_defence({
  move,
  offencePokemon,
  deffencePokemon,
  rateMapper,
  loopHitNumber,
  otherSetting,
  isCritical = false,
}: {
  move: Move;
  offencePokemon: PokemonV2;
  deffencePokemon: PokemonV2;
  rateMapper: damageRateMapper;
  loopHitNumber: number;
  otherSetting: any;
  isCritical?: boolean;
}) {
  // 【5】防御の補正値
  let defense =
    move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL || isPsyshockMove(move)
      ? deffencePokemon.effective_value.defense
      : deffencePokemon.effective_value.special_defense;
  const isKatayaburiMove = isKataYaburiMove(move);
  const isPhotonGeyser = isPhotonGeyserMove(move);
  const isPsyshock = isPsyshockMove(move);

    

  // テラバーストは攻撃/特攻の高い方に合わせて防御/特防側を選ぶ
  let tempMoveDamageClassNumber = move.damage_class_number
  const okAdjustTerabrast = isTerabrastMove(move) && isTerabrastMove(offencePokemon.selected_move) && offencePokemon.terasu_type &&
    calcRankedValue(offencePokemon.effective_value.attack, (isTennen(deffencePokemon) && !isKatayaburiMove) ? 0 : offencePokemon.rankCorrection.attack) >
    calcRankedValue(offencePokemon.effective_value.special_attack, (isTennen(deffencePokemon) && !isKatayaburiMove) ? 0 : offencePokemon.rankCorrection.special_attack)
  if (okAdjustTerabrast) {
    defense = deffencePokemon.effective_value.defense
    tempMoveDamageClassNumber=MOVE_DAMAGE_CLASS_PHYSICAL
  }

  // フォトンゲイザーの動的判定
  const photonGeyserAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove)
    ? 0
    : (offencePokemon.rankCorrection?.attack ?? 0);
  const photonGeyserSpecialAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove)
    ? 0
    : (offencePokemon.rankCorrection?.special_attack ?? 0);
  const okAdjustPhotonGeyser = isPhotonGeyser &&
    calcRankedValue(offencePokemon.effective_value.attack, photonGeyserAttackRank) >
    calcRankedValue(offencePokemon.effective_value.special_attack, photonGeyserSpecialAttackRank)
  if (okAdjustPhotonGeyser) {
    defense = deffencePokemon.effective_value.defense
    tempMoveDamageClassNumber=MOVE_DAMAGE_CLASS_PHYSICAL
  }

  // Apply rank correction to defense (急所時は防御側のプラス補正を0にする、せいなるつるぎ・DDラリアット・なしくずしは全ランク補正無視、てんねんは全ランク補正無視、かたやぶり技はてんねん無視)
  const ignoreAllRanks = isThroughRankMove(move) || (isTennen(deffencePokemon) && !isKatayaburiMove); // ランク補正無視技、てんねん（かたやぶり技除く）
  
  if (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustTerabrast || okAdjustPhotonGeyser || isPsyshock) {
    let defenseRank = deffencePokemon.rankCorrection.defense;
    if (ignoreAllRanks) {
      defenseRank = 0;
    } else if (isCritical && defenseRank > 0) {
      defenseRank = 0;
    }
    defense = calcRankedValue(defense, defenseRank);
  } else if (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL) {
    let specialDefenseRank = deffencePokemon.rankCorrection.special_defense;
    if (ignoreAllRanks) {
      specialDefenseRank = 0;
    } else if (isCritical && specialDefenseRank > 0) {
      specialDefenseRank = 0;
    }
    defense = calcRankedValue(defense, specialDefenseRank);
  }

  // 防御補正倍率は4096基準で積算
  let localRate = 4096;
  // 特性
  if (
    isWazawainoturugi(offencePokemon) &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL||okAdjustTerabrast||okAdjustPhotonGeyser) &&
    !isWazawainoturugi(deffencePokemon) // 相互無効化
  ) {
    localRate = Math.round(localRate * per_075);
  } else if (
    isWazawainoutama(offencePokemon) &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL || isPsyshock) &&
    !isWazawainoutama(deffencePokemon) // 相互無効化
  ) {
    localRate = Math.round(localRate * per_075);
  }

  // くさのけがわ特性 + グラスフィールドでの防御1.5倍補正
  if (
    isKusanokegawa(deffencePokemon) &&
    otherSetting.battle_field === BATTLE_FIELD_GLASS &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustTerabrast || okAdjustPhotonGeyser || isPsyshock)
  ) {
    localRate = Math.round(localRate * per_150);
  }

  // ファーコート特性での物理技に対する防御2倍補正
  if (
    isFurCoat(deffencePokemon) &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustTerabrast || okAdjustPhotonGeyser || isPsyshock)
  ) {
    localRate = Math.round(localRate * 2.0);
  }

  // ふしぎなうろこ特性 + 状態異常での防御1.5倍補正
  if (
    isMarvelScale(deffencePokemon) &&
    hasPokemonStatus(deffencePokemon.other_setting, 'status-ailment') &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustTerabrast || okAdjustPhotonGeyser || isPsyshock)
  ) {
    localRate = Math.round(localRate * per_150);
  }

  // こだいかっせい・クォークチャージ特性での防御1.3倍補正
  if (
    hasKodaiOrQuark(deffencePokemon) &&
    deffencePokemon.other_setting?.kodai_defense_stat === 'defense' &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustTerabrast || okAdjustPhotonGeyser || isPsyshock)
  ) {
    localRate = Math.round(localRate * per_130);
  }

  // こだいかっせい・クォークチャージ特性での特防1.3倍補正
  if (
    hasKodaiOrQuark(deffencePokemon) &&
    deffencePokemon.other_setting?.kodai_defense_stat === 'special_defense' &&
    tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL
  ) {
    localRate = Math.round(localRate * per_130);
  }


  if (
    tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL &&
    !isPsyshock &&
    deffencePokemon.selected_item_id ===
    DEFENCE_ITEM_ID_ASSAULT_VEST
  ) {
    localRate = Math.round(localRate * per_150);
  } else if (
    deffencePokemon.selected_item_id ===
    DEFENCE_ITEM_ID_SINKANOKISEKI &&
    isNotLastEvolvePokemon(deffencePokemon)
  ) {
    localRate = Math.round(localRate * per_150);
  }

  let finalDefence = calc_五捨五超入_gosutegoire((defense * localRate) / 4096);
  if (finalDefence < 1) {
    finalDefence = 1;
  }

  return finalDefence;
}
